import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";

// Global masking config — persisted in localStorage.
//
// Two knobs:
//   enabled       — master switch. When false, MaskedValue / DomainCell render
//                   plain text and skip the eye toggle entirely.
//   charsToMask   — how many characters to bullet-out from the CENTER of each
//                   value. Visible chars split evenly between start + end.
//
// Surfaced via the <MaskingConfigProvider> at main.tsx and consumed by
// <MaskedValue> + <DomainCell> via useMaskingConfig().

const LS_ENABLED = "wptw:mask:enabled";
const LS_CHARS = "wptw:mask:chars";
const DEFAULT_ENABLED = true;
const DEFAULT_CHARS = 10;
const MIN_CHARS = 1;
const MAX_CHARS = 100;
const BULLET_CHAR = "•";

export interface MaskingConfig {
  enabled: boolean;
  charsToMask: number;
}

interface MaskingContextShape extends MaskingConfig {
  setEnabled: (v: boolean) => void;
  setCharsToMask: (n: number) => void;
}

const MaskingContext = createContext<MaskingContextShape | null>(null);

function readEnabled(): boolean {
  try {
    const v = window.localStorage.getItem(LS_ENABLED);
    if (v === null) return DEFAULT_ENABLED;
    return v === "true";
  } catch { return DEFAULT_ENABLED; }
}
function readChars(): number {
  try {
    const v = Number(window.localStorage.getItem(LS_CHARS) ?? DEFAULT_CHARS);
    if (!Number.isFinite(v) || v < MIN_CHARS) return DEFAULT_CHARS;
    return Math.min(MAX_CHARS, Math.floor(v));
  } catch { return DEFAULT_CHARS; }
}

export function MaskingConfigProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(readEnabled);
  const [charsToMask, setCharsState] = useState<number>(readChars);

  const setEnabled = useCallback((v: boolean) => {
    setEnabledState(v);
    try { window.localStorage.setItem(LS_ENABLED, String(v)); } catch { /* ignore */ }
  }, []);
  const setCharsToMask = useCallback((n: number) => {
    const clamped = Math.max(MIN_CHARS, Math.min(MAX_CHARS, Math.floor(n)));
    setCharsState(clamped);
    try { window.localStorage.setItem(LS_CHARS, String(clamped)); } catch { /* ignore */ }
  }, []);

  // Listen for changes in OTHER tabs/windows so masking stays consistent.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_ENABLED) setEnabledState(readEnabled());
      else if (e.key === LS_CHARS) setCharsState(readChars());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => ({ enabled, charsToMask, setEnabled, setCharsToMask }), [enabled, charsToMask, setEnabled, setCharsToMask]);
  return <MaskingContext.Provider value={value}>{children}</MaskingContext.Provider>;
}

export function useMaskingConfig(): MaskingContextShape {
  const ctx = useContext(MaskingContext);
  if (!ctx) {
    // Fallback (e.g. component rendered outside the provider in a test):
    // return the defaults with no-op setters.
    return {
      enabled: DEFAULT_ENABLED,
      charsToMask: DEFAULT_CHARS,
      setEnabled: () => {},
      setCharsToMask: () => {},
    };
  }
  return ctx;
}

// Center-mask a string: keep the outer characters, replace the middle N with
// bullets. The chars-to-mask value is the global setting from the provider.
//
// Examples (charsToMask = 10):
//   "https://wp.mairan-site7.stage.twinfrarun.com" (44 chars)
//     → "https://wp.mairan-si••••••••••wnfrarun.com"
//   "example.com" (11 chars)
//     → "e••••••••••"   (length-1 visible because mask >= length-1)
//   "ab" (shorter than mask)
//     → "••"
export function maskCenter(input: string | null | undefined, charsToMask: number): string {
  const s = String(input ?? "");
  if (!s) return "";
  const n = Math.max(0, Math.floor(charsToMask));
  if (n <= 0) return s;
  if (n >= s.length) return BULLET_CHAR.repeat(s.length);
  const visible = s.length - n;
  const left = Math.ceil(visible / 2);
  const right = visible - left;
  return s.slice(0, left) + BULLET_CHAR.repeat(n) + s.slice(s.length - right);
}

export const MASK_LIMITS = { min: MIN_CHARS, max: MAX_CHARS, default: DEFAULT_CHARS };
